import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Search, Download } from "lucide-react";

const STATUS_COLOR = {
  "Received": "text-blue-400 border-blue-400/40",
  "Pending Audit": "text-yellow-400 border-yellow-400/40",
  "In Transit": "text-primary border-primary/40",
  "Data Destroyed": "text-destructive border-destructive/40",
  "Recycled": "text-secondary border-secondary/40",
  "Resold": "text-secondary border-secondary/40",
};

const FILTERS = ["All", "Received", "Pending Audit", "In Transit", "Data Destroyed", "Recycled", "Resold"];

export default function Assets() {
  const [assets, setAssets] = useState([]);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/assets").then((r) => setAssets(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    return assets.filter((a) => (filter === "All" || a.status === filter) &&
      (a.tag.toLowerCase().includes(q.toLowerCase()) || a.category.toLowerCase().includes(q.toLowerCase())));
  }, [assets, filter, q]);

  const downloadCert = async (asset) => {
    try {
      const res = await api.get(`/assets/${asset.id}/certificate`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificate-${asset.tag}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Certificate downloaded — ${asset.tag}`);
    } catch {
      toast.error("Certificate unavailable");
    }
  };

  return (
    <div data-testid="assets-page">
      <div className="mb-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-2">/ Chain of custody</p>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">Asset Tracking</h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex items-center gap-2 border border-border px-3 flex-1 bg-card">
          <Search size={16} className="text-muted-foreground" />
          <input
            data-testid="asset-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="SEARCH TAG OR CATEGORY"
            className="bg-transparent py-3 w-full font-mono text-xs uppercase tracking-wider placeholder:text-muted-foreground/60 focus:outline-none"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="asset-filter" className="flex items-center justify-between gap-2 border border-border bg-card px-4 py-3 font-mono text-xs uppercase tracking-wider min-w-[180px] hover:border-primary transition-colors">
              {filter} <ChevronDown size={14} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="rounded-none w-[180px]">
            {FILTERS.map((f) => (
              <DropdownMenuItem key={f} onClick={() => setFilter(f)} className="rounded-none font-mono text-xs uppercase tracking-wider" data-testid={`filter-${f.toLowerCase().replace(/\s/g, "-")}`}>
                {f}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="border border-border overflow-x-auto thin-scroll">
        <table className="w-full text-left border-collapse min-w-[640px]">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              {["Asset Tag", "Category", "Description", "Location", "Status", "Certificate"].map((h) => (
                <th key={h} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center font-mono text-xs text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center font-mono text-xs text-muted-foreground" data-testid="assets-empty">No assets match your filter.</td></tr>
            ) : rows.map((a) => (
              <tr key={a.id} data-testid="asset-row" className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 font-mono text-sm text-primary">{a.tag}</td>
                <td className="px-4 py-3 font-sans text-sm">{a.category}</td>
                <td className="px-4 py-3 font-sans text-sm text-muted-foreground">{a.description}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{a.location}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block font-mono text-[10px] uppercase tracking-wider border px-2 py-1 ${STATUS_COLOR[a.status] || "text-muted-foreground border-border"}`}>
                    {a.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {a.status === "Data Destroyed" ? (
                    <button
                      onClick={() => downloadCert(a)}
                      data-testid={`cert-download-${a.tag}`}
                      className="inline-flex items-center gap-1.5 border border-primary text-primary px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-primary hover:text-white transition-colors"
                    >
                      <Download size={12} /> PDF
                    </button>
                  ) : (
                    <span className="font-mono text-[10px] text-muted-foreground/50">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="font-mono text-[11px] text-muted-foreground mt-3">{rows.length} of {assets.length} assets</p>
    </div>
  );
}
